import { forwardRef, ReactNode, useEffect, useState } from 'react';
import { Box, Button, Step, StepLabel, Stepper, Typography } from '@mui/material';
import NameStep from './NameStep';
import MeasurementPointStep from "./MeasurementPointStep";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import TemplateSelectionStep from "./TemplateSelectionStep";
import {TemplateData, TemplateMetadata} from "../../pages/app/types.ts";
import {Template} from "@pdfme/common";

interface TemplateStepperProps {
    templates: TemplateMetadata[];
    blueprints: TemplateMetadata[];
    initialData?: TemplateData | null | undefined;
    onSave: (template : TemplateData) => void;
    onClose: () => void;
}

const TemplateStepper = forwardRef<HTMLDivElement, TemplateStepperProps>(
    ({ templates, blueprints, initialData, onSave, onClose }, ref) => {
        const { t } = useTranslation();
        const [activeStep, setActiveStep] = useState(0);
        const [template, setTemplate] = useState<TemplateData>({
            id: null,
            name: '',
            template: {} as Template,
            measurementPoints: []
        });

        const steps = [
            t('stepper.step.template.label').toString(),
            t('stepper.step.name.label').toString(),
            t('stepper.step.measurementPoint.label'),
            t('stepper.step.overview.label')
        ];

        useEffect(() => {
            if (initialData) {
                setActiveStep(1);
                setTemplate(initialData)
            }else{
                setActiveStep(0);
            }
        }, []);

        const handleNext = () => setActiveStep(prev => prev + 1);
        const handleBack = () => setActiveStep(prev => prev - 1);

        return (
            <Box
                tabIndex={-1}
                ref={ref}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '70%', md: '50%' },
                    maxHeight: '90%',
                    bgcolor: 'background.paper',
                    borderRadius: 4,
                    boxShadow: 24,
                    overflow: 'auto',
                    p: 2
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 16 }}>
                        {initialData? t('stepper.titleEdit') : t('stepper.titleAdd')}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {steps.map((label) => {
                        const labelProps: { optional?: ReactNode } = {};
                        return (
                            <Step
                                key={label}
                            >
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>

                <Box sx={{ overflow: 'auto' }}>
                    {activeStep === 0 && (
                        <TemplateSelectionStep
                            templates={templates}
                            blueprints={blueprints}
                            template={template}
                            setTemplate={setTemplate}
                            handleNext={handleNext}
                        />
                    )}
                    {activeStep === 1 && (
                        <NameStep
                            template={template}
                            setTemplate={setTemplate}
                        />
                    )}
                    {activeStep === 2 && (
                        <MeasurementPointStep
                            mode=""
                            template={template}
                            setTemplate={setTemplate}
                        />
                    )}
                    {activeStep === 3 && (
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            <MeasurementPointStep
                                mode={"overview"}
                                template={template}
                                setTemplate={setTemplate}
                            />
                        </Box>

                    )}
                </Box>
                {activeStep !== 0 &&
                    <Box sx={{ flex: 0.1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Button
                            disabled={
                                activeStep === 0 ||
                                (activeStep === 1 && Boolean(initialData))
                            }
                            onClick={handleBack}
                            sx={{ mr: 1, color: "#53b2ef" }}
                        >
                            {t('stepper.back')}
                        </Button>
                        <Button
                            sx={{mr: 1, color: "#53b2ef"}}
                            onClick={async () => {
                                if (activeStep === steps.length - 1) {
                                    onSave(template);
                                    onClose();
                                } else {
                                    handleNext();
                                }
                            }}
                        >
                            {activeStep === steps.length - 1 ? t('stepper.finish') : t('stepper.next')}
                        </Button>
                    </Box>
                }
            </Box>
        );
    }
);

export default TemplateStepper;
